import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { PickerChangeHandlerContext } from "@mui/x-date-pickers/internals/hooks/usePicker/usePickerValue.types";
import { DateRange, DateRangeValidationError } from "@mui/x-date-pickers-pro";
import { Dayjs } from "dayjs";
import { useDidMount, useForceRerender } from "hooks";

import { UseDateRangePickerParams } from "./DateRangePicker.types";

export const useDateRangePicker = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseDateRangePickerParams<R>) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const dateRangePickerRef = useRef<HTMLElement | null>(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [state, setState] = useState<DateRange<Dayjs>>(
    value ?? defaultValue ?? [null, null]
  );
  const forceRerender = useForceRerender();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: dateRangePickerRef.current,
        value: state,
        setValue: (v: DateRange<Dayjs>) => {
          setState(v);
        },
        reset: () => {
          setState([null, null]);
        },
      };
    }) as unknown as () => R,
    [dateRangePickerRef.current, state]
  );

  useDidMount(function init() {
    dateRangePickerRef.current =
      rootRef.current?.querySelector(".MuiFormControl-root") ?? null;
    forceRerender();
  });

  const onChangeHandler = (
    newValue: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<DateRangeValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const onCalendarCloseHandler = () => setOpenCalendar(false);

  const toggleCalendar = (event: React.PointerEvent) => {
    event.stopPropagation();
    setOpenCalendar((prev) => !prev);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    rootRef,
    dateRangePickerRef,
    valueState,
    openCalendar,
    onChangeHandler,
    onCalendarCloseHandler,
    toggleCalendar,
  };
};
